import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../../context/AuthContext';
import { getRequestCountsByStatus, getRequestsByRealtor } from '../../../services/serviceRequestService';
import { getCommissionSummary } from '../../../services/commissionService';
import { getReferralStats } from '../../../services/referralStatsService';
import { SERVICE_CATEGORIES } from '../../../config/ieumConfig';
import Spinner from '../../../components/common/Spinner';

const categoryLabel = (r) => {
  const services = Array.isArray(r.services) ? r.services : null;
  if (services && services.length > 0) {
    const first = SERVICE_CATEGORIES[services[0]]?.label || services[0];
    return services.length === 1 ? first : `${first} 외 ${services.length - 1}건`;
  }
  const k = r.category || r.service_category || '';
  return SERVICE_CATEGORIES[k]?.label || k || '-';
};

const DB_FILTERS = ['전체', '상담DB', '상담', '접수', '진행', '완료', '취소'];

const STATUS_LABEL = {
  unread: '상담요청',
  read: '상담',
  assigned: '접수',
  settlement_check: '진행',
  settlement_done: '완료',
  cancelled: '취소',
  hq_review_needed: '보류',
};

// ───── 컴포넌트 ─────
const LeadManage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [dbFilter, setDbFilter] = useState('전체');
  const [counts, setCounts] = useState({});
  const [summary, setSummary] = useState({ total: 0, withdrawable: 0, settled: 0 });
  const [referralStats, setReferralStats] = useState({ sent: 0, joined: 0, expiring: 0, expired: 0 });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) return;
    let alive = true;
    setLoading(true);
    Promise.allSettled([
      getRequestCountsByStatus(userData.uid).then(setCounts),
      getCommissionSummary(userData.uid).then(setSummary),
      getReferralStats(userData.uid).then(setReferralStats),
      getRequestsByRealtor(userData.uid).then(setRequests),
    ]).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userData?.uid]);

  if (loading) return <Spinner fullPage={false} />;

  const filteredRequests = requests.filter((r) => {
    if (dbFilter === '전체') return true;
    const s = r.hqStatus || 'unread';
    if (dbFilter === '상담DB') return s === 'unread';
    if (dbFilter === '상담') return s === 'read';
    if (dbFilter === '접수') return s === 'assigned';
    if (dbFilter === '진행') return s === 'settlement_check' && !r.serviceCompleted;
    if (dbFilter === '완료') return r.serviceCompleted || s === 'settlement_done';
    if (dbFilter === '취소') return s === 'cancelled' || s === 'unmatched_cancelled' || s === 'hq_review_needed';
    return true;
  });

  const REFERRAL_STATUS = [
    { label: '01. 추천발송', count: referralStats.sent },
    { label: '02. 추천회원', count: referralStats.joined },
  ];

  // hq_status 7단계 매핑
  const unreadOnly = Math.max(0, (counts.unread || 0) - (counts.read || 0));
  const TRACK_STATUS = [
    { label: '01. 상담요청', count: unreadOnly },
    { label: '02. 상담', count: counts.read || 0 },
    { label: '03. 접수', count: counts.assigned || 0 },
    { label: '04. 진행', count: counts.pending || 0 },
    { label: '05.보류/ 06.취소', count: (counts.hq_review_needed || 0) + (counts.cancelled || 0) },
    { label: '07. 완료', count: counts.completed || 0 },
  ];

  return (
    <Wrap>
      {/* 상단 카드 */}
      <TopArea>
        <TopCardFull>
          <TopCardLabel>총 고객 DB 건수</TopCardLabel>
          <TopCardValueDark>{counts.total || 0} 건</TopCardValueDark>
        </TopCardFull>

        <TopCardRow>
          <TopCard>
            <TopCardLabel>총 수익금</TopCardLabel>
            <TopCardValueDark>{summary.total.toLocaleString()} 원</TopCardValueDark>
          </TopCard>
          <TopCard onClick={() => navigate('/realtor/mypage/withdraw')} style={{ cursor: 'pointer' }}>
            <TopCardLabel>출금 가능 수수료 수익</TopCardLabel>
            <TopCardValueBlue>{summary.withdrawable.toLocaleString()} 원</TopCardValueBlue>
            <TopCardSub>선택 시 수익금 출금신청 화면으로 이동</TopCardSub>
          </TopCard>
        </TopCardRow>
      </TopArea>

      {/* 고객 DB 추적 현황 */}
      <SectionTitle>고객 DB 추적 현황</SectionTitle>
      <StatusCard>
        <StatusGrid $cols={3}>
          {TRACK_STATUS.map((s) => (
            <StatusCell key={s.label}>
              <CellLabel>{s.label}</CellLabel>
              <CellValue>{s.count}</CellValue>
            </StatusCell>
          ))}
        </StatusGrid>
      </StatusCard>

      {/* 공인중개사 DB 추적 현황 */}
      <Divider />
      <SectionTitle>공인중개사 DB 추적 현황</SectionTitle>
      <StatusCard>
        <StatusGrid $cols={2}>
          {REFERRAL_STATUS.map((s) => (
            <StatusCell key={s.label}>
              <CellLabel>{s.label}</CellLabel>
              <CellValue>{s.count}</CellValue>
            </StatusCell>
          ))}
        </StatusGrid>
      </StatusCard>

      {/* 링크 2개 */}
      <LinkSection>
        <LinkItem onClick={() => navigate('/realtor/manage/track-history')}>
          <LinkLeft>
            <LinkLabel>고객 DB 추적 현황 내역</LinkLabel>
            <LinkDesc>상태 구분별 건수 · 변경일시 기준 고객 리스트</LinkDesc>
          </LinkLeft>
          <LinkArrow>&gt;</LinkArrow>
        </LinkItem>
        <LinkItem onClick={() => navigate('/realtor/mypage/stats')}>
          <LinkLeft>
            <LinkLabel>고객 DB 실적 통계</LinkLabel>
            <LinkDesc>년도/월 상태별 검색, 일별·월별 내역</LinkDesc>
          </LinkLeft>
          <LinkArrow>&gt;</LinkArrow>
        </LinkItem>
      </LinkSection>

      {/* 수익금 정산내역 */}
      <SettlementSection>
        <SettlementTitle>수익금 정산내역</SettlementTitle>
        <SettlementLine>전환리워드 {(summary.conversion || 0).toLocaleString()} 원</SettlementLine>
        <SettlementLineBold>추천리워드 {(summary.referral || 0).toLocaleString()} 원 (5%, 1년 한정)</SettlementLineBold>
        <SettlementLine>미정산 {summary.unsettledCount || 0} 건</SettlementLine>
        <SettlementLine>출금: 매월 말일(익월 정산분 기준) 신청 가능</SettlementLine>
        <SettlementLine>세금: 개인 3.3%, 사업자 10%</SettlementLine>
      </SettlementSection>

      {/* 고객 DB 추적 */}
      <SectionTitle>고객 DB 추적</SectionTitle>
      <ChipRow>
        {DB_FILTERS.map((f) => (
          <Chip key={f} $active={dbFilter === f} onClick={() => setDbFilter(f)}>
            {f}
          </Chip>
        ))}
      </ChipRow>

      {filteredRequests.length === 0 ? (
        <EmptyBox>
          <EmptyIcon>!</EmptyIcon>
          <EmptyText>표시할 데이터가 없습니다.</EmptyText>
        </EmptyBox>
      ) : (
        <RequestList>
          {filteredRequests.slice(0, 20).map((r) => (
            <RequestItem key={r.id} onClick={() => navigate('/realtor/manage/track-history')}>
              <RequestLeft>
                <RequestName>{r.customerName || '-'}</RequestName>
                <RequestMeta>{categoryLabel(r)} · {r.createdAt || ''}</RequestMeta>
              </RequestLeft>
              <RequestStatus>{STATUS_LABEL[r.hqStatus] || r.hqStatus}</RequestStatus>
            </RequestItem>
          ))}
        </RequestList>
      )}

      {/* 하단 링크 (플로팅 아님) */}
      <BottomLinkSection>
        <LinkItem onClick={() => navigate('/realtor/manage/referral-members')}>
          <LinkLeft>
            <LinkLabel>추천 가입회원 조회</LinkLabel>
            <LinkDesc>
              {referralStats.joined > 0
                ? `추천 가입 ${referralStats.joined}명 · 발송 ${referralStats.sent}건`
                : '추천 내역이 없습니다.'}
            </LinkDesc>
          </LinkLeft>
          <LinkArrow>&gt;</LinkArrow>
        </LinkItem>
      </BottomLinkSection>
    </Wrap>
  );
};

export default LeadManage;

// ───── 스타일 ─────
const Wrap = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TopArea = styled.div`
  margin: -${({ theme }) => theme.realtor.pagePadding} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.realtor.pagePadding} ${({ theme }) => theme.spacing.base};
  background: #F3F4F6;
`;

const TopCardFull = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const TopCardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const TopCard = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const TopCardLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const TopCardValueDark = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const TopCardValueBlue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const TopCardSub = styled.p`
  font-size: 9px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const Divider = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.borderLight};
  margin: ${({ theme }) => theme.spacing.lg} -${({ theme }) => theme.realtor.pagePadding};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.base};
`;

const StatusCard = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.base};
  margin: 0 -${({ theme }) => theme.realtor.pagePadding};
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => `repeat(${$cols}, 1fr)`};
  gap: ${({ theme }) => theme.spacing.md};
`;

const StatusCell = styled.div`
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 44px;
  justify-content: center;
`;

const CellLabel = styled.span`
  font-size: 10px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  white-space: nowrap;
`;

const CellValue = styled.span`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

/* 링크 */
const LinkSection = styled.div`
  margin: ${({ theme }) => theme.spacing.base} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: 0 ${({ theme }) => theme.realtor.pagePadding};
  border-top: 6px solid ${({ theme }) => theme.colors.borderLight};
`;

const LinkItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.base} 0;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: none;
  cursor: pointer;
  text-align: left;

  &:last-child { border-bottom: none; }
`;

const LinkLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const LinkLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const LinkDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-top: 2px;
`;

const LinkArrow = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-shrink: 0;
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

/* 수익금 정산내역 */
const SettlementSection = styled.div`
  margin: 0 -${({ theme }) => theme.realtor.pagePadding};
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.realtor.pagePadding};
  background: #F3F4F6;
`;

const SettlementTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SettlementLine = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.8;
`;

const SettlementLineBold = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.8;
`;

/* 고객 DB 추적 필터 */
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Chip = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : '#fff'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.text};
  border: ${({ $active, theme }) => $active ? 'none' : `1px solid ${theme.colors.border}`};
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
`;

const RequestList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const RequestItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const RequestLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const RequestName = styled.p`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const RequestMeta = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 2px;
`;

const RequestStatus = styled.span`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.borderLight};
  padding: 4px 10px;
  border-radius: 999px;
  flex-shrink: 0;
  margin-left: 8px;
`;

/* 하단 링크 */
const BottomLinkSection = styled.div`
  margin: 0 -${({ theme }) => theme.realtor.pagePadding};
  padding: 0 ${({ theme }) => theme.realtor.pagePadding};
  border-top: 6px solid ${({ theme }) => theme.colors.borderLight};
`;
